import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/best-accessibility-audit-tools';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideCategory {
  title: string;
  body: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function BestAccessibilityAuditToolsGuidePage() {
  const { t } = useTranslation('marketing');
  const categories = t('pages.bestAccessibilityAuditToolsGuide.categories.items', {
    returnObjects: true,
  }) as GuideCategory[];
  const featureChecklist = t('pages.bestAccessibilityAuditToolsGuide.features.items', {
    returnObjects: true,
  }) as string[];
  const buyerQuestions = t('pages.bestAccessibilityAuditToolsGuide.choosing.questions', {
    returnObjects: true,
  }) as string[];
  const sections = t('pages.bestAccessibilityAuditToolsGuide.sections', { returnObjects: true }) as GuideSection[];
  const automatedSections = sections.slice(0, 2);
  const closingSections = sections.slice(2);
  const sidebarPoints = t('pages.bestAccessibilityAuditToolsGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.bestAccessibilityAuditToolsGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.bestAccessibilityAuditToolsGuide.meta.title')}
      metaDescription={t('pages.bestAccessibilityAuditToolsGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.bestAccessibilityAuditToolsGuide.hero.eyebrow')}
      title={t('pages.bestAccessibilityAuditToolsGuide.hero.title')}
      intro={t('pages.bestAccessibilityAuditToolsGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.bestAccessibilityAuditToolsGuide.hero.title'),
          description: t('pages.bestAccessibilityAuditToolsGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.bestAccessibilityAuditToolsGuide.sidebar.title')}</h2>
            <p>{t('pages.bestAccessibilityAuditToolsGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.bestAccessibilityAuditToolsGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.bestAccessibilityAuditToolsGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.bestAccessibilityAuditToolsGuide.intro.title')}</h2>
        <p>{t('pages.bestAccessibilityAuditToolsGuide.intro.body')}</p>
        <p>{t('pages.bestAccessibilityAuditToolsGuide.intro.note')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.bestAccessibilityAuditToolsGuide.toolsDo.title')}</h2>
        <p>{t('pages.bestAccessibilityAuditToolsGuide.toolsDo.body')}</p>
        <ul className="acc-public-page-list">
          {(t('pages.bestAccessibilityAuditToolsGuide.toolsDo.points', { returnObjects: true }) as string[]).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="audit-tool-categories-title">
        <div>
          <h2 id="audit-tool-categories-title">{t('pages.bestAccessibilityAuditToolsGuide.categories.title')}</h2>
          <p>{t('pages.bestAccessibilityAuditToolsGuide.categories.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {categories.map((category) => (
            <article key={category.title} className="acc-public-page-subcard">
              <h3>{category.title}</h3>
              <p>{category.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.bestAccessibilityAuditToolsGuide.features.title')}</h2>
        <ul className="acc-public-page-list" aria-label={t('pages.bestAccessibilityAuditToolsGuide.features.ariaLabel')}>
          {featureChecklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {automatedSections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.bestAccessibilityAuditToolsGuide.choosing.title')}</h2>
        <p>{t('pages.bestAccessibilityAuditToolsGuide.choosing.body')}</p>
        <ul className="acc-public-page-list">
          {buyerQuestions.map((question) => (
            <li key={question}>{question}</li>
          ))}
        </ul>
      </section>

      {closingSections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.bestAccessibilityAuditToolsGuide.disclaimer.title')}</h2>
        <p>{t('pages.bestAccessibilityAuditToolsGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="audit-tools-guide-cta-title">
        <div>
          <h2 id="audit-tools-guide-cta-title">{t('pages.bestAccessibilityAuditToolsGuide.cta.title')}</h2>
          <p>{t('pages.bestAccessibilityAuditToolsGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
